/**
 * Kinu Calm storefront behavior for kc-* sections.
 * - Header cart icon opens Horizon's cart drawer; cart count stays in sync.
 * - Product section: gallery, sticky mobile bar and the 3-step "Build your set" dialog
 *   (designs → extras → your set). Lines are added with /cart/add.js; "add to cart" also
 *   dispatches the standard CartLinesUpdateEvent so Horizon's cart drawer refreshes and opens.
 * - Before/after slider.
 */

const CART_LINES_UPDATE = 'shopify:cart:lines-update';
const ROOT = () => window.Shopify?.routes?.root || '/';

/** @type {Promise<any> | null} */
let shopifyEventsPromise = null;
function loadShopifyEvents() {
  if (!shopifyEventsPromise) {
    shopifyEventsPromise = import('@shopify/events').catch((error) => {
      console.warn('[kinu-calm] @shopify/events unavailable', error);
      return null;
    });
  }
  return shopifyEventsPromise;
}

function getCartDrawer() {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  return drawer && typeof drawer.open === 'function' ? drawer : null;
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

/* ---------- Cart icon + count ---------- */

function setCartCount(count) {
  for (const el of document.querySelectorAll('[data-kc-cart-count]')) {
    el.textContent = String(count);
    el.hidden = count === 0;
  }
}

async function fetchCart() {
  const response = await fetch(`${ROOT()}cart.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

async function refreshCartCount() {
  try {
    const cart = await fetchCart();
    setCartCount(cart.item_count);
  } catch (error) {
    console.warn('[kinu-calm] could not refresh cart count', error);
  }
}

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-kc-cart-open]') : null;
  if (!trigger) return;
  const drawer = getCartDrawer();
  if (!drawer) return;
  event.preventDefault();
  drawer.open();
});

document.addEventListener(CART_LINES_UPDATE, (event) => {
  const promise = /** @type {any} */ (event).promise;
  if (promise && typeof promise.then === 'function') {
    promise.then(refreshCartCount, () => {});
  } else {
    refreshCartCount();
  }
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshCartCount();
});

/* ---------- Money ---------- */

function formatMoney(cents, format) {
  const value = Number(cents) / 100;
  const fmt = typeof format === 'string' && format ? format : '${{amount}}';
  const withDelimiters = (number, decimals, thousands, decimal) => {
    const [whole, fraction] = number.toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return withDelimiters(value, 0, ',', '.');
      case 'amount_with_comma_separator':
        return withDelimiters(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withDelimiters(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return withDelimiters(value, 2, "'", '.');
      default:
        return withDelimiters(value, 2, ',', '.');
    }
  });
}

/**
 * Adds lines to the cart. When `notifyDrawer` is true, dispatches the standard
 * CartLinesUpdateEvent from `source` so Horizon's cart drawer re-renders and opens.
 * @param {{id: number, quantity: number}[]} items
 * @param {{ source?: Element, notifyDrawer?: boolean }} [options]
 */
async function addLinesToCart(items, { source, notifyDrawer = false } = {}) {
  const events = notifyDrawer ? await loadShopifyEvents() : null;
  const CartLinesUpdateEvent = events?.CartLinesUpdateEvent;
  let deferred = null;
  let settled = false;

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);

  if (CartLinesUpdateEvent && source) {
    deferred = CartLinesUpdateEvent.createPromise();
    source.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );
  }

  try {
    const body = { items };
    if (sectionIds.length) body.sections = sectionIds.join(',');
    const response = await fetch(`${ROOT()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    const cart = await fetchCart();
    setCartCount(cart.item_count);

    if (result.status) {
      settled = true;
      deferred?.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'kc-builder' },
      });
      throw new Error(result.description || result.message || 'Add to cart failed');
    }

    settled = true;
    deferred?.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        source: 'kc-builder',
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        sections: result.sections,
      },
    });
    return cart;
  } catch (error) {
    if (deferred && !settled) deferred.reject(error);
    throw error;
  }
}

/* ---------- Set builder ---------- */

class KinuBuilder {
  /**
   * @param {HTMLDialogElement} dialog
   * @param {any} data - parsed [data-kc-builder-json]
   */
  constructor(dialog, data) {
    this.dialog = dialog;
    this.data = data;
    this.text = JSON.parse(dialog.querySelector('[data-kc-builder-text]')?.textContent || '{}');
    this.strings = data.strings || {};
    this.designs = (data.designs || []).map((d, index) => ({ ...d, key: String(index) }));
    this.extras = (data.extras || []).map((e, index) => ({ ...e, key: `x${index}` }));
    this.steps = this.extras.length ? ['designs', 'extras', 'review'] : ['designs', 'review'];
    this.step = 'designs';
    this.filter = 'all';
    /** @type {Map<string, number>} design key → quantity */
    this.qty = new Map();
    /** @type {Map<string, number>} extra key → quantity */
    this.extrasQty = new Map();
    this.busy = false;
    /** Kits and subtotal (cents) already in the cart, so prices and shipping continue from there. */
    this.cartKits = 0;
    this.cartSubtotal = 0;
    this.cartItems = [];
    this.cartQty = new Map();

    this.$ = (selector) => dialog.querySelector(selector);
    this.grid = this.$('[data-kc-b-grid]');
    this.body = this.$('[data-kc-b-body]');

    this.restoreState();
    this.bind();
    // Read the cart right away (not only when the builder opens), so the grid order and the
    // bottom bar are already up to date the moment the customer opens it.
    this.syncCart(true);
    this.renderPricing();
    this.renderFilters();
    this.renderGrid();
    this.renderExtras();
    this.update();
  }

  money(cents) {
    return formatMoney(cents, this.data.moneyFormat);
  }

  /* ----- state ----- */

  get kitCount() {
    let count = 0;
    for (const n of this.qty.values()) count += n;
    return count;
  }

  get designsSubtotal() {
    let sum = 0;
    for (const design of this.designs) sum += (this.qty.get(design.key) || 0) * design.price;
    return sum;
  }

  get designsCompareSubtotal() {
    let sum = 0;
    for (const design of this.designs) {
      const unit = design.compareAt > design.price ? design.compareAt : design.price;
      sum += (this.qty.get(design.key) || 0) * unit;
    }
    return sum;
  }

  get extrasSubtotal() {
    let sum = 0;
    for (const extra of this.extras) sum += (this.extrasQty.get(extra.key) || 0) * extra.price;
    return sum;
  }

  get savings() {
    const s = this.data.savings || {};
    if (!s.enabled || !s.amount || !this.kitCount) return 0;
    // Matches the "Every extra kit $29.90" app discount (kits 2 to 50), counting kits already in the cart.
    if (s.mode === 'each_extra') {
      const max = s.maxQty || 20;
      const extras = (n) => Math.max(0, Math.min(n, max) - 1);
      return (extras(this.cartKits + this.kitCount) - extras(this.cartKits)) * s.amount;
    }
    if (this.kitCount < s.minQty) return 0;
    if (s.mode === 'per_set') return Math.floor(this.kitCount / s.minQty) * s.amount;
    return s.amount;
  }

  get itemsTotal() {
    return Math.max(0, this.designsSubtotal + this.extrasSubtotal - this.savings);
  }

  /** What counts toward free shipping: this set plus what is already in the cart. */
  get orderSubtotal() {
    return this.itemsTotal + this.cartSubtotal;
  }

  /** Shipping in cents: the flat fee below the free-shipping amount, otherwise 0. */
  get shippingCost() {
    const s = this.data.shipping || {};
    if (!s.fee || !s.freeMin) return 0;
    return this.orderSubtotal >= s.freeMin ? 0 : s.fee;
  }

  get total() {
    return this.orderSubtotal + this.shippingCost;
  }

  /** Reads the cart so a second visit to the builder continues from the kits already picked. */
  async syncCart(force = false) {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const kitVariants = new Set(this.designs.map((d) => Number(d.variantId)));
      let kits = 0;
      // Kits per variant already in the cart, so the grid can mark them and step 3 can list them.
      this.cartQty = new Map();
      for (const item of cart.items || []) {
        if (!kitVariants.has(Number(item.variant_id))) continue;
        kits += item.quantity;
        this.cartQty.set(Number(item.variant_id), (this.cartQty.get(Number(item.variant_id)) || 0) + item.quantity);
      }
      this.cartItems = cart.items || [];
      const changed = force || kits !== this.cartKits || cart.total_price !== this.cartSubtotal;
      this.cartKits = kits;
      this.cartSubtotal = cart.total_price || 0;
      if (!changed) return;
      this.renderGrid();
      this.renderExtras();
      if (this.step === 'review') this.renderReview();
      this.update();
    } catch (e) {
      /* offline or blocked: keep the builder working without the cart */
    }
  }

  shipNoteHtml() {
    const s = this.data.shipping || {};
    if (!s.freeMin || !s.fee) return '';
    const sum = this.orderSubtotal;
    const pct = Math.min(100, Math.round((sum / s.freeMin) * 100));
    const done = sum >= s.freeMin;
    const text = done
      ? this.strings.shipDone || ''
      : (this.strings.shipTodo || '').replace('[amount]', this.money(sum)).replace('[missing]', this.money(s.freeMin - sum));
    return `<div class="kc-shipnote${done ? ' is-done' : ''}"><p>${escapeHtml(text)}</p><div class="kc-shipnote__bar"><span style="width:${pct}%"></span></div></div>`;
  }

  /** Price of the next kit when it unlocks the bundle saving (e.g. the 2nd kit), otherwise null. */
  secondKitPrice(design) {
    const s = this.data.savings || {};
    if (!s.enabled || !s.amount || (this.kitCount === 0 && this.cartKits === 0)) return null;
    if (s.mode === 'each_extra') return Math.max(0, design.price - s.amount);
    if (s.mode !== 'once' || this.kitCount !== s.minQty - 1) return null;
    return Math.max(0, design.price - s.amount);
  }

  /** Small one-time popup inside the builder after the first kit is picked. */
  showDealPopup() {
    const s = this.data.savings || {};
    const pop = this.$('[data-kc-b-deal]');
    if (!pop || !s.enabled || !s.amount || this.dealShown) return;
    this.dealShown = true;
    const first = this.designs.find((d) => (this.qty.get(d.key) || 0) > 0) || this.designs[0];
    const text = pop.querySelector('[data-kc-b-deal-text]');
    if (text && first) {
      text.textContent = (this.strings.dealText || '')
        .replace('[price]', this.money(Math.max(0, first.price - s.amount)))
        .replace('[full]', this.money(first.price));
    }
    pop.hidden = false;
    pop.querySelector('[data-kc-b-deal-ok]')?.focus({ preventScroll: true });
  }

  /** Big view of one design (photo, category, short description, price, add). */
  openViewer(key) {
    const viewer = this.$('[data-kc-b-viewer]');
    const design = this.designs.find((d) => d.key === key);
    if (!viewer || !design) return;
    this.viewerKey = key;
    const src = design.imageLarge || design.image;
    viewer.querySelector('[data-kc-b-viewer-img]').innerHTML = src ? `<img src="${escapeHtml(src)}" alt="${escapeHtml(design.name)}">` : '';
    viewer.querySelector('[data-kc-b-viewer-cat]').textContent = design.category || '';
    viewer.querySelector('[data-kc-b-viewer-title]').textContent = design.name;
    viewer.querySelector('[data-kc-b-viewer-text]').textContent = design.text || '';
    viewer.hidden = false;
    this.renderViewerAction();
    viewer.querySelector('[data-kc-b-viewer-close]')?.focus({ preventScroll: true });
  }

  renderViewerAction() {
    const viewer = this.$('[data-kc-b-viewer]');
    const design = this.designs.find((d) => d.key === this.viewerKey);
    if (!viewer || viewer.hidden !== false || !design) return;
    const n = this.qty.get(design.key) || 0;
    const deal = this.secondKitPrice(design);
    viewer.querySelector('[data-kc-b-viewer-price]').innerHTML =
      deal !== null && n === 0
        ? `<s>${escapeHtml(this.money(design.price))}</s> <strong>${escapeHtml(this.money(deal))}</strong> ${escapeHtml(this.strings.secondLabel || '')}`
        : `<strong>${escapeHtml(this.money(design.price))}</strong>`;
    const action = viewer.querySelector('[data-kc-b-viewer-action]');
    if (!design.available) {
      action.innerHTML = `<button type="button" class="kc-btn kc-btn--secondary kc-btn--block" disabled>${escapeHtml(this.strings.soldOut)}</button>`;
    } else if (n === 0) {
      action.innerHTML = `<button type="button" class="kc-btn kc-btn--primary kc-btn--block" data-kc-b-action="add" data-key="${design.key}">+ ${escapeHtml(this.strings.add)}</button>`;
    } else {
      action.innerHTML = `<div class="kc-viewer__qty"><div class="kc-stepper-qty" role="group" aria-label="${escapeHtml(design.name)}">
          <button type="button" data-kc-b-action="dec" data-key="${design.key}" aria-label="−">−</button>
          <span aria-live="polite">${n}</span>
          <button type="button" data-kc-b-action="inc" data-key="${design.key}" aria-label="+">+</button>
        </div><button type="button" class="kc-btn kc-btn--secondary" data-kc-b-viewer-close>${escapeHtml(this.strings.done || 'Done')}</button></div>`;
    }
  }

  closeViewer() {
    const viewer = this.$('[data-kc-b-viewer]');
    if (viewer) viewer.hidden = true;
    this.viewerKey = null;
  }

  hideDealPopup() {
    const pop = this.$('[data-kc-b-deal]');
    if (pop) pop.hidden = true;
  }

  kitsLabel(n) {
    return `${n} ${n === 1 ? this.strings.kit : this.strings.kits}`;
  }

  setQty(key, value) {
    const design = this.designs.find((d) => d.key === key);
    if (!design || !design.available) return;
    const next = Math.max(0, Math.min(99, value));
    const before = this.kitCount;
    if (next === 0) this.qty.delete(key);
    else this.qty.set(key, next);
    const after = this.kitCount;
    const minQty = this.data.savings?.minQty || 2;
    const each = this.data.savings?.mode === 'each_extra';
    // Crossing the deal line changes every card's price line.
    const dealLine = (n) => (each ? n + this.cartKits > 0 : n === minQty - 1);
    if (dealLine(before) !== dealLine(after)) this.renderGrid();
    else this.renderCard(key);
    this.update();
    if (before === 0 && after === 1 && this.cartKits === 0) this.showDealPopup();
  }

  /* ----- events ----- */

  bind() {
    this.$('[data-kc-b-close]')?.addEventListener('click', () => this.close());
    this.$('[data-kc-b-back]')?.addEventListener('click', () => this.go(this.steps[this.steps.indexOf(this.step) - 1]));
    this.$('[data-kc-b-next]')?.addEventListener('click', () => this.next());
    this.$('[data-kc-b-cart]')?.addEventListener('click', () => this.submit('cart'));
    this.$('[data-kc-b-help]')?.addEventListener('click', () => this.helpMeChoose());

    this.dialog.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target === this.dialog) return; // backdrop: keep open (full screen)

      if (target.closest('[data-kc-b-deal-ok], [data-kc-b-deal-close]')) return this.hideDealPopup();

      const view = target.closest('[data-kc-b-view]');
      if (view) return this.openViewer(view.getAttribute('data-kc-b-view') || '');
      if (target.closest('[data-kc-b-viewer-close]') || target.matches('[data-kc-b-viewer]')) return this.closeViewer();

      const goto = target.closest('[data-kc-b-goto]');
      if (goto) return this.go(goto.getAttribute('data-kc-b-goto'));

      const filter = target.closest('[data-kc-b-filter]');
      if (filter) {
        this.filter = filter.getAttribute('data-kc-b-filter') || 'all';
        this.renderFilters();
        this.renderGrid();
        return;
      }

      if (target.closest('[data-kc-b-clear]')) {
        // "Clear" on the "picked earlier" note: start over with an empty set.
        this.qty.clear();
        this.extrasQty.clear();
        this.restoredKeys = [];
        this.clearState();
        this.renderGrid();
        this.renderExtras();
        this.update();
        return;
      }

      const action = target.closest('[data-kc-b-action]');
      if (!action) return;
      this.touched = true;
      const key = action.getAttribute('data-key') || '';
      const current = this.qty.get(key) || 0;
      switch (action.getAttribute('data-kc-b-action')) {
        case 'add':
        case 'inc':
          this.setQty(key, current + 1);
          break;
        case 'dec':
          this.setQty(key, current - 1);
          break;
        case 'remove':
          this.setQty(key, 0);
          break;
        case 'extra-toggle':
        case 'extra-add':
          this.addExtra(key);
          break;
        case 'extra-dec':
          this.setExtraQty(key, (this.extrasQty.get(key) || 0) - 1);
          break;
        case 'extra-remove':
          this.setExtraQty(key, 0);
          break;
        case 'cart-change': {
          // A line that is already in the cart: change it there, then re-read the cart.
          const quantity = Math.max(0, Number(action.getAttribute('data-qty')) || 0);
          action.disabled = true;
          fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity }),
          })
            .then(() => this.syncCart(true))
            .finally(() => {
              action.disabled = false;
            });
          return;
        }
      }
      if (this.step === 'review') this.renderReview();
    });

    this.dialog.addEventListener('close', () => this.unlockPage());
    // Escape closes the big design view first, not the whole builder.
    this.dialog.addEventListener('cancel', (event) => {
      if (this.$('[data-kc-b-viewer]')?.hidden === false) {
        event.preventDefault();
        this.closeViewer();
      }
    });
  }

  /* A set that is not in the cart yet survives closing the builder or leaving the page
     (kept in this browser for 3 days, keyed by variant so it works on every kit page). */
  static STATE_KEY = 'kc-builder-set';

  saveState() {
    try {
      const d = {};
      const x = {};
      for (const design of this.designs) if (this.qty.get(design.key)) d[design.variantId] = this.qty.get(design.key);
      for (const extra of this.extras) if (this.extrasQty.get(extra.key)) x[extra.variantId] = this.extrasQty.get(extra.key);
      // A design preselected only because its page was opened is not remembered until the customer acts.
      const onlyAutoPick =
        !this.touched && this.autoPick && Object.keys(x).length === 0 && this.qty.size === 1 && this.qty.has(this.autoPick);
      if (onlyAutoPick || (!Object.keys(d).length && !Object.keys(x).length)) localStorage.removeItem(KinuBuilder.STATE_KEY);
      else localStorage.setItem(KinuBuilder.STATE_KEY, JSON.stringify({ d, x, s: this.step, t: Date.now() }));
    } catch (e) {
      /* storage blocked (private mode): the builder still works, it just won't remember */
    }
  }

  restoreState() {
    try {
      const saved = JSON.parse(localStorage.getItem(KinuBuilder.STATE_KEY) || 'null');
      if (!saved || Date.now() - (saved.t || 0) > 3 * 24 * 3600 * 1000) return;
      for (const design of this.designs) {
        const n = Number(saved.d?.[design.variantId]) || 0;
        if (n > 0 && design.available) this.qty.set(design.key, Math.min(n, 99));
      }
      for (const extra of this.extras) {
        const n = Number(saved.x?.[extra.variantId]) || 0;
        if (n > 0 && extra.available) this.extrasQty.set(extra.key, Math.min(n, 99));
      }
      if (this.steps.includes(saved.s)) this.resumeStep = saved.s;
      this.restoredKeys = [...this.qty.keys()];
    } catch (e) {
      /* unreadable or blocked storage: start fresh */
    }
  }

  clearState() {
    this.resumeStep = null;
    this.restoredKeys = [];
    try {
      localStorage.removeItem(KinuBuilder.STATE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  open(designName) {
    this.autoPick = null;
    if (designName) {
      const wanted = String(designName).toLowerCase();
      const design = this.designs.find((d) => d.name.toLowerCase() === wanted || (d.handle && d.handle.toLowerCase() === wanted));
      if (design && design.available && !this.qty.get(design.key)) {
        this.autoPick = design.key;
        this.setQty(design.key, 1);
      }
    }
    // Designs already in the set go to the top of the grid (after the ones in the cart).
    // Fixed when the builder opens, so cards don't jump while the customer is picking.
    this.pinned = new Set(this.qty.keys());
    this.renderGrid();
    // Reopen where the customer left off, unless they came here to add a specific design.
    const resume = !designName && this.kitCount > 0 && this.resumeStep ? this.resumeStep : 'designs';
    this.go(resume);
    this.syncCart();
    this.lockPage();
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    this.body?.scrollTo({ top: 0 });
    this.wakeImages();
  }

  close() {
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else this.dialog.removeAttribute('open');
    this.unlockPage();
  }

  lockPage() {
    document.documentElement.classList.add('kc-scroll-lock');
  }

  unlockPage() {
    document.documentElement.classList.remove('kc-scroll-lock');
  }

  go(step) {
    if (!step || !this.steps.includes(step)) return;
    this.step = step;
    // The free-shipping note depends on the kits picked so far.
    if (step === 'extras') this.renderExtras();
    for (const panel of this.dialog.querySelectorAll('[data-kc-b-step]')) {
      panel.hidden = panel.getAttribute('data-kc-b-step') !== step;
    }
    const index = this.steps.indexOf(step);
    this.steps.forEach((name, i) => {
      const dot = this.dialog.querySelector(`[data-kc-b-dot="${name}"]`);
      dot?.classList.toggle('is-current', i === index);
      dot?.classList.toggle('is-done', i < index);
    });
    const back = this.$('[data-kc-b-back]');
    if (back) back.hidden = index === 0;
    if (step === 'review') this.renderReview();
    this.update();
    this.body?.scrollTo({ top: 0 });
  }

  async next() {
    const index = this.steps.indexOf(this.step);
    if (this.step === 'review') return this.submit('checkout');
    if (this.busy || this.kitCount + this.cartKits < (this.data.minQty || 1)) return;
    // Leaving step 1 puts the chosen kits in the real cart: Shopify (and the Meta pixel) count it as an
    // add to cart, abandoned-cart emails can reach the customer, and the rest of the builder already
    // knows how to show and price kits that are in the cart.
    if (this.step === 'designs' && this.kitCount > 0 && !(await this.moveKitsToCart())) return;
    this.go(this.steps[index + 1]);
  }

  /** Adds the picked kits to the cart and turns them into "in cart" kits. Returns false on failure. */
  async moveKitsToCart() {
    const items = [];
    for (const design of this.designs) {
      const n = this.qty.get(design.key) || 0;
      if (n > 0) items.push({ id: design.variantId, quantity: n });
    }
    if (!items.length) return true;
    const error = this.$('[data-kc-b-error]');
    if (error) error.textContent = '';
    this.busy = true;
    this.dialog.setAttribute('aria-busy', 'true');
    this.update();
    try {
      await addLinesToCart(items);
      this.qty.clear();
      this.saveState();
      await this.syncCart(true);
      return true;
    } catch (err) {
      console.error('[kinu-calm] builder add failed', err);
      if (error) error.textContent = (err instanceof Error && err.message) || this.strings.error;
      return false;
    } finally {
      this.busy = false;
      this.dialog.removeAttribute('aria-busy');
      this.renderGrid();
      this.update();
    }
  }

  helpMeChoose() {
    const picks = this.designs.filter((d) => d.bestseller && d.available);
    const want = Math.max(this.data.minQty || 1, this.data.savings?.enabled ? this.data.savings.minQty : 1);
    for (const design of picks.slice(0, want)) {
      if (!this.qty.get(design.key)) this.setQty(design.key, 1);
    }
    this.filter = 'all';
    this.renderFilters();
    this.renderGrid();
    this.$('[data-kc-b-next]')?.focus({ preventScroll: true });
  }

  async submit(mode) {
    if (this.busy || this.kitCount + this.cartKits < (this.data.minQty || 1)) return;
    const items = [];
    for (const design of this.designs) {
      const n = this.qty.get(design.key) || 0;
      if (n > 0) items.push({ id: design.variantId, quantity: n });
    }
    for (const extra of this.extras) {
      const n = this.extrasQty.get(extra.key) || 0;
      if (n > 0) items.push({ id: extra.variantId, quantity: n });
    }
    // Kits went into the cart at step 1, so there may be nothing left to add.
    if (!items.length) {
      if (!this.cartKits) return;
      this.clearState();
      if (mode === 'checkout') {
        window.location.href = this.data.checkoutUrl || '/checkout';
        return;
      }
      this.close();
      const drawer = getCartDrawer();
      if (drawer && !drawer.isOpen) drawer.open();
      else if (!drawer) window.location.href = `${ROOT()}cart`;
      return;
    }

    const error = this.$('[data-kc-b-error]');
    if (error) error.textContent = '';
    this.busy = true;
    this.dialog.setAttribute('aria-busy', 'true');
    const nextButton = /** @type {HTMLButtonElement | null} */ (this.$('[data-kc-b-next]'));
    if (nextButton) nextButton.disabled = true;

    try {
      if (mode === 'checkout') {
        await addLinesToCart(items);
        // The set is in the cart now: don't bring it back on the next visit.
        this.clearState();
        window.location.href = this.data.checkoutUrl || '/checkout';
        return;
      }
      await addLinesToCart(items, { source: this.dialog.closest('[data-kc-product]') || document.body, notifyDrawer: true });
      this.qty.clear();
      this.extrasQty.clear();
      this.clearState();
      this.renderGrid();
      this.renderExtras();
      this.close();
      const drawer = getCartDrawer();
      if (drawer && !drawer.isOpen) drawer.open();
      else if (!drawer) window.location.href = `${ROOT()}cart`;
    } catch (err) {
      console.error('[kinu-calm] builder add failed', err);
      if (error) error.textContent = (err instanceof Error && err.message) || this.strings.error;
    } finally {
      this.busy = false;
      this.dialog.removeAttribute('aria-busy');
      this.update();
    }
  }

  /* ----- rendering ----- */

  renderPricing() {
    const el = this.$('[data-kc-b-pricing]');
    if (!el || !this.designs.length) return;
    const cheapest = this.designs.reduce((a, b) => (b.price < a.price ? b : a));
    const s = this.data.savings || {};
    let html = `<span class="kc-builder__price"><strong>${escapeHtml(this.money(cheapest.price))}</strong>`;
    if (cheapest.compareAt > cheapest.price) html += ` <s>${escapeHtml(this.money(cheapest.compareAt))}</s>`;
    html += ` ${escapeHtml(this.text.each || '')}</span>`;
    if (cheapest.compareAt > cheapest.price) {
      const off = Math.round(((cheapest.compareAt - cheapest.price) / cheapest.compareAt) * 100);
      html += `<span class="kc-badge kc-badge--sm kc-badge--off">-${off}%</span>`;
    }
    if (s.enabled && s.amount && this.strings.badge) {
      html += `<span class="kc-badge kc-badge--sm">${escapeHtml(this.strings.badge)}</span>`;
    }
    el.innerHTML = html;

    const help = this.$('[data-kc-b-help]');
    if (help) help.hidden = !this.designs.some((d) => d.bestseller && d.available);
  }

  renderFilters() {
    const el = this.$('[data-kc-b-filters]');
    if (!el) return;
    // Filters keep the order of the theme setting; only those with at least one design are shown.
    const used = new Set(this.designs.flatMap((d) => (d.categories && d.categories.length ? d.categories : [d.category])).filter(Boolean));
    const ordered = (this.data.filters || []).filter((label) => used.has(label));
    const categories = ordered.length ? ordered : [...used];
    if (categories.length < 2) {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    const chip = (value, label) =>
      `<button type="button" class="kc-chip${this.filter === value ? ' is-active' : ''}" data-kc-b-filter="${escapeHtml(value)}" aria-pressed="${this.filter === value}">${escapeHtml(label)}</button>`;
    el.innerHTML = chip('all', this.text.all || 'All') + categories.map((c) => chip(c, c)).join('');
  }

  visibleDesigns() {
    const list =
      this.filter === 'all'
        ? this.designs
        : this.designs.filter((d) => (d.categories || []).includes(this.filter) || d.category === this.filter);
    const pinned = this.pinned || new Set();
    if (!this.cartQty.size && !pinned.size) return list;
    // Designs already in the cart come first, then the ones in the set, so a returning customer sees them straight away.
    const rank = (d) => (this.cartQty.get(Number(d.variantId)) ? 0 : pinned.has(d.key) ? 1 : 2);
    return [...list].sort((a, b) => rank(a) - rank(b));
  }

  cardHtml(design) {
    const n = this.qty.get(design.key) || 0;
    // Cards are ~160px wide on phones: let the browser pick a smaller CDN size there.
    const sized = (w) => design.image.replace(/([?&]width=)\d+/, `$1${w}`);
    const srcset = /[?&]width=\d+/.test(design.image || '')
      ? ` srcset="${escapeHtml(`${sized(360)} 360w, ${sized(480)} 480w, ${sized(640)} 640w`)}" sizes="(min-width: 700px) 280px, 46vw"`
      : '';
    const image = design.image
      ? `<img src="${escapeHtml(design.image)}"${srcset} alt="" loading="lazy" decoding="async" width="640" height="800">`
      : `<span class="kc-placeholder">[${escapeHtml(design.name)} photo]</span>`;
    let action;
    if (!design.available) {
      action = `<button type="button" class="kc-btn kc-btn--secondary kc-btn--sm" disabled>${escapeHtml(this.strings.soldOut)}</button>`;
    } else if (n === 0) {
      action = `<button type="button" class="kc-btn kc-btn--secondary kc-btn--sm" data-kc-b-action="add" data-key="${design.key}" aria-label="${escapeHtml(`${this.strings.add} ${design.name}`)}">+ ${escapeHtml(this.strings.add)}</button>`;
    } else {
      action = `<div class="kc-stepper-qty" role="group" aria-label="${escapeHtml(design.name)}">
          <button type="button" data-kc-b-action="dec" data-key="${design.key}" aria-label="−">−</button>
          <span aria-live="polite">${n}</span>
          <button type="button" data-kc-b-action="inc" data-key="${design.key}" aria-label="+">+</button>
        </div>`;
    }
    // Price line: once one kit is picked, the other designs show the 2nd-kit price.
    const deal = this.secondKitPrice(design);
    const priceLine =
      deal !== null && n === 0
        ? `<p class="kc-dcard__price is-deal"><s>${escapeHtml(this.money(design.price))}</s> <strong>${escapeHtml(this.money(deal))}</strong> ${escapeHtml(this.strings.secondLabel || '')}</p>`
        : `<p class="kc-dcard__price">${escapeHtml(this.money(design.price))}${design.compareAt > design.price ? ` <s>${escapeHtml(this.money(design.compareAt))}</s>` : ''}</p>`;
    const off = design.compareAt > design.price ? Math.round(((design.compareAt - design.price) / design.compareAt) * 100) : 0;
    const offBadge = off ? `<span class="kc-dcard__off">-${off}%</span>` : '';
    const inCart = this.cartQty.get(Number(design.variantId)) || 0;
    const cartBadge = inCart
      ? `<span class="kc-dcard__incart">✓ ${escapeHtml(this.strings.inCartShort || 'in cart')}${inCart > 1 ? ` ×${inCart}` : ''}</span>`
      : '';
    return `<article class="kc-dcard${n ? ' is-selected' : ''}${inCart ? ' is-in-cart' : ''}" data-card="${design.key}">
        <button type="button" class="kc-dcard__img" data-kc-b-view="${design.key}" aria-label="${escapeHtml(`${this.strings.view || 'View'} ${design.name}`)}">${image}${n ? '<span class="kc-dcard__check" aria-hidden="true">✓</span>' : ''}${cartBadge}${offBadge}<span class="kc-dcard__zoom" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="11" cy="11" r="6.5"/><path d="M16 16l4.5 4.5M11 8.5v5M8.5 11h5"/></svg>${escapeHtml(this.strings.view || 'View')}</span></button>
        <div class="kc-dcard__body">
          <h3 class="kc-dcard__name">${escapeHtml(design.name)}</h3>
          ${design.subtitle ? `<p class="kc-dcard__sub">${escapeHtml(design.subtitle)}</p>` : ''}
          ${priceLine}
        </div>
        <div class="kc-dcard__action">${action}</div>
      </article>`;
  }

  renderGrid() {
    if (!this.grid) return;
    const list = this.visibleDesigns();
    this.grid.innerHTML = list.map((d) => this.cardHtml(d)).join('');
    const line = this.$('[data-kc-b-count-line]');
    if (line) line.textContent = (this.text.countLine || '[count]').replace('[count]', String(list.length));
    // A set remembered from an earlier visit: say so, with a way to start over.
    let note = this.$('[data-kc-b-restored]');
    const restored = (this.restoredKeys || []).filter((k) => this.qty.get(k));
    if (restored.length) {
      if (!note) {
        note = document.createElement('p');
        note.className = 'kc-builder__restored';
        note.setAttribute('data-kc-b-restored', '');
        this.grid.before(note);
      }
      const names = restored.map((k) => this.designs.find((d) => d.key === k)?.name).filter(Boolean);
      note.innerHTML = `${escapeHtml(this.strings.pickedEarlier || 'You picked these earlier (not in your cart yet):')} <strong>${escapeHtml(names.join(', '))}</strong> <button type="button" class="kc-builder__restored-clear" data-kc-b-clear>${escapeHtml(this.strings.clear || 'Clear')}</button>`;
      note.hidden = false;
    } else if (note) {
      note.hidden = true;
    }
    this.wakeImages();
  }

  // Native lazy loading does not fire reliably inside the modal dialog: load the design
  // photos ourselves as they approach the visible part of the builder's scroll area.
  wakeImages() {
    if (!this.grid) return;
    const imgs = this.grid.querySelectorAll('img[loading="lazy"]');
    if (!('IntersectionObserver' in window)) {
      imgs.forEach((img) => (img.loading = 'eager'));
      return;
    }
    this.imgObserver?.disconnect();
    this.imgObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.loading = 'eager';
          this.imgObserver.unobserve(entry.target);
        }
      },
      { root: this.body || null, rootMargin: '600px 0px' }
    );
    imgs.forEach((img) => this.imgObserver.observe(img));
  }

  renderCard(key) {
    const card = this.grid?.querySelector(`[data-card="${key}"]`);
    const design = this.designs.find((d) => d.key === key);
    if (!card || !design) return;
    const wrapper = document.createElement('div');
    wrapper.innerHTML = this.cardHtml(design);
    const fresh = wrapper.firstElementChild;
    // The card is on screen (it was just tapped): its photo must show immediately.
    fresh?.querySelectorAll('img').forEach((img) => (img.loading = 'eager'));
    if (fresh) card.replaceWith(fresh);
    // Keep keyboard focus on the stepper after the first add.
    fresh?.querySelector('[data-kc-b-action="inc"], [data-kc-b-action="add"]')?.focus({ preventScroll: true });
  }

  /** Adds an extra. A "per kit" extra (the frame) jumps straight to one per kit, then +1. */
  addExtra(key) {
    const extra = this.extras.find((x) => x.key === key);
    if (!extra || !extra.available) return;
    const current = this.extrasQty.get(key) || 0;
    if (extra.single && current >= 1) return;
    const perKit = extra.perKit ? Math.max(1, this.kitCount) : 1;
    this.setExtraQty(key, current < perKit ? perKit : current + 1);
  }

  setExtraQty(key, n) {
    if (n > 0) this.extrasQty.set(key, Math.min(n, 99));
    else this.extrasQty.delete(key);
    this.renderExtras();
    this.update();
  }

  renderExtras() {
    const el = this.$('[data-kc-b-extras]');
    if (!el) return;
    el.innerHTML = this.shipNoteHtml() + this.extras
      .map((extra) => {
        const n = this.extrasQty.get(extra.key) || 0;
        const added = n > 0;
        const image = extra.image
          ? `<img src="${escapeHtml(extra.image)}" alt="" loading="eager" decoding="async">`
          : `<span class="kc-placeholder">[${escapeHtml(extra.title)} photo]</span>`;
        return `<article class="kc-extra${added ? ' is-selected' : ''}">
            <div class="kc-extra__img">${image}</div>
            <div class="kc-extra__body">
              <div class="kc-extra__head"><h3>${escapeHtml(extra.title)}</h3><span>+${escapeHtml(this.money(extra.price))}</span></div>
              ${extra.text ? `<p>${escapeHtml(extra.text)}</p>` : ''}
              ${
                added
                  ? `<div class="kc-extra__qty">
                      <button type="button" class="kc-line__btn" data-kc-b-action="extra-dec" data-key="${extra.key}" aria-label="−">−</button>
                      <span>✓ ${n} ${escapeHtml(this.text.inTheBox)}</span>
                      <button type="button" class="kc-line__btn" data-kc-b-action="extra-add" data-key="${extra.key}" aria-label="+" ${extra.available && !extra.single ? '' : 'disabled'}>+</button>
                    </div>`
                  : `<button type="button" class="kc-btn kc-btn--secondary kc-btn--sm kc-btn--block" data-kc-b-action="extra-add" data-key="${extra.key}" ${extra.available ? '' : 'disabled'}>+ ${escapeHtml(this.text.addToBox)}${
                      extra.perKit && this.kitCount > 1 ? ` (×${this.kitCount})` : ''
                    }</button>`
              }
            </div>
          </article>`;
      })
      .join('');
  }

  renderReview() {
    const lines = this.$('[data-kc-b-lines]');
    if (lines) {
      const rows = [];
      // Items added on an earlier visit: listed first, changed directly in the cart.
      const trash = '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"/></svg>';
      if (this.cartItems.length) {
        rows.push(`<li class="kc-lines__label">${escapeHtml(this.strings.inCart || 'In your cart')}</li>`);
        for (const item of this.cartItems) {
          const src = item.image ? `${item.image}${String(item.image).includes('?') ? '&' : '?'}width=120` : '';
          const img = src ? `<img src="${escapeHtml(src)}" alt="" loading="eager" decoding="async">` : '';
          rows.push(`<li class="kc-line kc-line--cart">
            <div class="kc-line__img">${img}</div>
            <div class="kc-line__info"><strong>${escapeHtml(String(item.product_title).split(' – ')[0])}</strong><span>${escapeHtml(this.money(item.final_line_price / Math.max(1, item.quantity)))}</span></div>
            <div class="kc-line__qty">
              <button type="button" class="kc-line__btn" data-kc-b-action="cart-change" data-key="${escapeHtml(item.key)}" data-qty="${item.quantity - 1}" aria-label="${escapeHtml(`${this.strings.remove} ${item.product_title}`)}">${item.quantity === 1 ? trash : '−'}</button>
              <span>${item.quantity}</span>
              <button type="button" class="kc-line__btn" data-kc-b-action="cart-change" data-key="${escapeHtml(item.key)}" data-qty="${item.quantity + 1}" aria-label="+">+</button>
            </div>
          </li>`);
        }
        if (this.kitCount || [...this.extrasQty.values()].some(Boolean)) rows.push(`<li class="kc-lines__label">${escapeHtml(this.strings.addingNow || 'Adding now')}</li>`);
      }
      // Show what each line really costs: after the first kit (counting the cart), kits get the extra-kit price.
      const sv = this.data.savings || {};
      const eachExtra = sv.enabled && sv.amount && sv.mode === 'each_extra';
      const maxKits = sv.maxQty || 20;
      let kitsSoFar = this.cartKits;
      for (const design of this.designs) {
        const n = this.qty.get(design.key) || 0;
        if (!n) continue;
        let discounted = 0;
        if (eachExtra) {
          for (let i = 0; i < n; i++) {
            const position = kitsSoFar + i + 1;
            if (position > 1 && position <= maxKits) discounted++;
          }
        }
        kitsSoFar += n;
        const full = design.price * n;
        const paid = full - discounted * sv.amount;
        const priceHtml = discounted
          ? `<s>${escapeHtml(this.money(full))}</s> <b class="kc-line__deal">${escapeHtml(this.money(paid))}</b>`
          : escapeHtml(this.money(full));
        rows.push(`<li class="kc-line">
            <div class="kc-line__img">${design.image ? `<img src="${escapeHtml(design.image)}" alt="" loading="eager" decoding="async">` : ''}</div>
            <div class="kc-line__info"><strong>${escapeHtml(design.name)}</strong><span>${priceHtml}</span></div>
            <div class="kc-line__qty">
              <button type="button" class="kc-line__btn" data-kc-b-action="${n === 1 ? 'remove' : 'dec'}" data-key="${design.key}" aria-label="${escapeHtml(`${this.strings.remove} ${design.name}`)}">${n === 1 ? '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"/></svg>' : '−'}</button>
              <span>${n}</span>
              <button type="button" class="kc-line__btn" data-kc-b-action="inc" data-key="${design.key}" aria-label="+">+</button>
            </div>
          </li>`);
      }
      for (const extra of this.extras) {
        const en = this.extrasQty.get(extra.key) || 0;
        if (!en) continue;
        rows.push(`<li class="kc-line">
            <div class="kc-line__img">${extra.image ? `<img src="${escapeHtml(extra.image)}" alt="" loading="eager" decoding="async">` : ''}</div>
            <div class="kc-line__info"><strong>${escapeHtml(extra.title)}</strong><span>${escapeHtml(this.money(extra.price))}</span></div>
            <div class="kc-line__qty">
              <button type="button" class="kc-line__btn" data-kc-b-action="${en === 1 ? 'extra-remove' : 'extra-dec'}" data-key="${extra.key}" aria-label="${escapeHtml(`${this.strings.remove} ${extra.title}`)}">${en === 1 ? '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"/></svg>' : '−'}</button>
              <span>${en}</span>
              <button type="button" class="kc-line__btn" data-kc-b-action="extra-add" data-key="${extra.key}" aria-label="+" ${extra.single ? 'disabled' : ''}>+</button>
            </div>
          </li>`);
      }
      lines.innerHTML = rows.join('');
    }

    const extraRows = this.$('[data-kc-b-extra-rows]');
    if (extraRows) {
      const offers = this.extras.filter((extra) => extra.available && !(extra.single && this.extrasQty.get(extra.key)));
      extraRows.innerHTML = offers
        .map(
          (extra) =>
            `<button type="button" class="kc-upsell__row" data-kc-b-action="extra-add" data-key="${extra.key}" aria-label="${escapeHtml(`${this.strings.add} ${extra.title}`)}">${
              extra.image ? `<img src="${escapeHtml(extra.image)}" alt="" loading="eager" decoding="async" width="52" height="52">` : ''
            }<span class="kc-upsell__name"><strong>${escapeHtml(extra.title)}</strong><span>${escapeHtml(this.money(extra.price))}${
              extra.compareAt > extra.price ? ` <s>${escapeHtml(this.money(extra.compareAt))}</s>` : ''
            }</span></span><span class="kc-upsell__add" aria-hidden="true">+ ${escapeHtml(this.strings.add)}</span></button>`
        )
        .join('');
      const box = this.$('[data-kc-b-upsell]');
      if (box) box.hidden = offers.length === 0;
    }

    const summary = this.$('[data-kc-b-summary]');
    if (summary) {
      const savings = this.savings;
      const compare = this.designsCompareSubtotal;
      // Kits at their own price (struck compare-at only if set); the bundle saving gets its own line.
      const showCompare = compare > this.designsSubtotal;
      let html = `<div class="kc-summary__row"><dt>${escapeHtml(this.kitsLabel(this.kitCount))}</dt><dd>${
        showCompare ? `<s>${escapeHtml(this.money(compare))}</s> ` : ''
      }${escapeHtml(this.money(this.designsSubtotal))}</dd></div>`;
      if (this.kitCount === 0 && this.cartSubtotal > 0) html = '';
      if (savings > 0) {
        html += `<div class="kc-summary__row kc-summary__row--save"><dt>${escapeHtml(this.text.saving)}</dt><dd>−${escapeHtml(this.money(savings))}</dd></div>`;
      }
      if (this.extrasSubtotal > 0) {
        html += `<div class="kc-summary__row"><dt>${escapeHtml(this.text.step2 || 'Extras')}</dt><dd>${escapeHtml(this.money(this.extrasSubtotal))}</dd></div>`;
      }
      if (this.cartSubtotal > 0) {
        const inCart = this.cartKits > 0 ? `${this.strings.inCart || 'In your cart'} (${this.kitsLabel(this.cartKits)})` : this.strings.inCart || 'In your cart';
        html += `<div class="kc-summary__row"><dt>${escapeHtml(inCart)}</dt><dd>${escapeHtml(this.money(this.cartSubtotal))}</dd></div>`;
      }
      const ship = this.shippingCost;
      html += `<div class="kc-summary__row"><dt>${escapeHtml(this.text.shipping)}</dt><dd>${escapeHtml(ship ? this.money(ship) : this.strings.shippingValue)}</dd></div>`;
      html += `<div class="kc-summary__row kc-summary__row--total"><dt>${escapeHtml(this.text.total)}</dt><dd>${escapeHtml(this.money(this.total))}</dd></div>`;
      summary.innerHTML = html;
    }
  }

  update() {
    this.saveState();
    const kits = this.kitCount;
    const minQty = this.data.minQty || 1;
    const s = this.data.savings || {};

    // Bottom bar: the 2nd-kit deal line, then the free-shipping progress line.
    const progress = this.$('[data-kc-b-progress]');
    if (progress) {
      const ship = this.data.shipping || {};
      // Also shown when only the cart has kits: the bar continues from what is already there.
      const show = kits > 0 || this.cartKits > 0;
      progress.hidden = !show;
      if (show) {
        const deal = this.$('[data-kc-b-progress-deal]');
        if (deal) {
          const missing = Math.max(0, s.minQty - kits - this.cartKits);
          const nextPrice = this.designs.length ? Math.max(0, this.designs[0].price - (s.amount || 0)) : 0;
          const dealText =
            s.enabled && s.amount && kits === 0 && s.mode === 'each_extra'
              ? (this.strings.addAnother || 'Add another kit for just [price] ♡').replace('[price]', this.money(nextPrice))
              : s.enabled && s.amount
              ? missing > 0
                ? (this.strings.nudge || '').replace('[n]', String(missing)).replace('[amount]', this.money(s.amount))
                : (this.strings.unlocked || '').replace('[amount]', this.money(this.savings))
              : '';
          deal.textContent = dealText;
          deal.hidden = !dealText;
        }
        const text = this.$('[data-kc-b-progress-text]');
        const fill = /** @type {HTMLElement | null} */ (this.$('[data-kc-b-progress-fill]'));
        if (ship.freeMin && ship.fee) {
          const sum = this.orderSubtotal;
          const done = sum >= ship.freeMin;
          if (text) {
            text.textContent = done
              ? this.strings.barShipDone || ''
              : (this.strings.barShipTodo || '').replace('[missing]', this.money(ship.freeMin - sum));
          }
          if (fill) fill.style.width = `${Math.min(100, (sum / ship.freeMin) * 100)}%`;
          progress.classList.toggle('is-done', done);
        } else if (fill) {
          fill.style.width = `${Math.min(100, (kits / (s.minQty || 1)) * 100)}%`;
        }
      }
    }

    this.renderViewerAction();

    const count = this.$('[data-kc-b-count]');
    if (count) {
      count.textContent =
        kits < minQty && kits > 0
          ? (this.strings.minimum || '').replace('[n]', String(minQty - kits))
          : kits > 0
            ? this.cartKits > 0
              ? `${this.kitsLabel(kits)} + ${this.cartKits} ${this.strings.inCartShort || 'in cart'}`
              : this.kitsLabel(kits)
            : this.cartKits > 0
              ? `${this.cartKits} ${this.strings.inCartShort || 'in cart'}`
              : '';
    }

    const labelEl = this.$('[data-kc-b-next-label]');
    const nextButton = /** @type {HTMLButtonElement | null} */ (this.$('[data-kc-b-next]'));
    const base = this.step === 'review' ? this.text.checkout : this.step === 'extras' ? this.text.review : this.text.continue;
    // Kits already in the cart count too: after step 1 the picked kits live in the cart.
    const allKits = kits + this.cartKits;
    if (labelEl) labelEl.textContent = kits > 0 || (this.step !== 'designs' && this.cartKits > 0) ? `${base} · ${this.money(this.total)}` : base;
    if (nextButton) nextButton.disabled = this.busy || allKits < minQty || (this.step === 'designs' && kits === 0 && !this.cartKits);

    const alt = this.$('[data-kc-b-cart]');
    if (alt) alt.hidden = this.step !== 'review' || allKits < minQty;

    if (this.step === 'review' && allKits === 0) this.go('designs');
  }
}

/* ---------- Product section ---------- */

class KinuProduct {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.sticky = root.querySelector('[data-kc-sticky]');
    this.mainCta = root.querySelector('[data-kc-main-cta]');

    const json = root.querySelector('[data-kc-builder-json]');
    const dialog = root.querySelector('[data-kc-builder]');
    if (json && dialog) {
      try {
        this.builder = new KinuBuilder(/** @type {HTMLDialogElement} */ (dialog), JSON.parse(json.textContent || '{}'));
        builders.push(this.builder);
      } catch (error) {
        console.warn('[kinu-calm] could not start the set builder', error);
      }
    }

    this.initGallery();
    this.initSticky();
  }

  initGallery() {
    this.slides = [...this.root.querySelectorAll('.kc-gallery__slide')];
    this.thumbs = [...this.root.querySelectorAll('[data-kc-thumb]')];
    for (const thumb of this.thumbs) {
      thumb.addEventListener('click', () => this.showSlide(Number(thumb.getAttribute('data-kc-thumb'))));
    }

    const main = this.root.querySelector('.kc-gallery__main');
    if (!main || this.slides.length < 2) return;
    let startX = 0;
    let startY = 0;
    main.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });
    main.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      const current = this.slides.findIndex((s) => s.classList.contains('is-active'));
      this.showSlide((current + (dx < 0 ? 1 : -1) + this.slides.length) % this.slides.length);
    }, { passive: true });
  }

  showSlide(index) {
    if (!this.slides?.length || Number.isNaN(index)) return;
    // Rotating kit photos keep their URL in data-src until needed: load the shown one and the next before swapping.
    const n = this.slides.length;
    for (const i of [index, (index + 1) % n, (index - 1 + n) % n]) {
      const img = this.slides[i].querySelector('img');
      if (!img) continue;
      if (img.dataset.srcset) { img.srcset = img.dataset.srcset; delete img.dataset.srcset; }
      if (img.dataset.src) { img.src = img.dataset.src; delete img.dataset.src; }
      img.loading = 'eager';
    }
    this.slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      if (!active) slide.querySelector('video')?.pause();
    });
    this.thumbs.forEach((thumb, i) => {
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
      if (i === index) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    });
  }

  initSticky() {
    if (!this.sticky || !this.mainCta || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.sticky.classList.toggle('is-visible', passed);
        this.sticky.setAttribute('aria-hidden', String(!passed));
        const button = this.sticky.querySelector('button');
        if (button) button.tabIndex = passed ? 0 : -1;
      },
      { threshold: 0 }
    );
    observer.observe(this.mainCta);
  }
}

/** @type {KinuBuilder[]} */
const builders = [];

// Any [data-kc-builder-open] on the page opens the builder (optionally with data-kc-design preselected).
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-kc-builder-open]') : null;
  if (!trigger || !builders.length) return;
  event.preventDefault();
  builders[0].open(trigger.getAttribute('data-kc-design') || undefined);
});

function initProducts(scope = document) {
  for (const root of scope.querySelectorAll('[data-kc-product]')) {
    if (root.dataset.kcReady) continue;
    root.dataset.kcReady = 'true';
    new KinuProduct(/** @type {HTMLElement} */ (root));
  }

  // Deep link from the homepage: ?build=1&design=Name
  const params = new URLSearchParams(window.location.search);
  if (builders.length && params.has('build') && !initProducts.opened) {
    initProducts.opened = true;
    builders[0].open(params.get('design') || undefined);
  }
}
initProducts.opened = false;

/* ---------- Before / after slider ---------- */

function initBeforeAfter(scope = document) {
  for (const root of scope.querySelectorAll('[data-kc-compare]')) {
    if (root.dataset.kcReady) continue;
    root.dataset.kcReady = 'true';
    const range = root.querySelector('input[type="range"]');
    if (!(range instanceof HTMLInputElement)) continue;
    const apply = () => root.style.setProperty('--kc-split', `${range.value}%`);
    range.addEventListener('input', apply);
    apply();
  }
}

/* ---------- Promo countdown (to the promo end date, else to midnight in the visitor's time zone) ---------- */

let promoTimer = 0;
function initPromoCountdown() {
  if (promoTimer || !document.querySelector('[data-kc-promo]')) return;
  const pad = (n) => String(n).padStart(2, '0');
  const tick = () => {
    const now = new Date();
    const endAttr = document.querySelector('[data-kc-promo-end]')?.getAttribute('data-kc-promo-end');
    const end = endAttr ? new Date(endAttr) : null;
    const hasEnd = end !== null && !Number.isNaN(end.getTime());
    const target = hasEnd ? end : new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const left = Math.max(0, Math.floor((target.getTime() - now.getTime()) / 1000));
    // With an end date the three units are days / hours / minutes, otherwise hours / minutes / seconds.
    const units = hasEnd
      ? [Math.floor(left / 86400), Math.floor((left % 86400) / 3600), Math.floor((left % 3600) / 60)]
      : [Math.floor(left / 3600), Math.floor((left % 3600) / 60), left % 60];
    const [a, b, c] = units.map(pad);
    const text = hasEnd ? `${a}d ${b}h ${c}m` : `${a}h ${b}m ${c}s`;
    // Re-query each tick: the builder and theme editor can re-render the boxes.
    for (const el of document.querySelectorAll('[data-kc-promo-time]')) el.textContent = text;
    // Black Friday style: one box per unit.
    for (const el of document.querySelectorAll('[data-kc-promo-h]')) el.textContent = a;
    for (const el of document.querySelectorAll('[data-kc-promo-m]')) el.textContent = b;
    for (const el of document.querySelectorAll('[data-kc-promo-s]')) el.textContent = c;
  };
  tick();
  promoTimer = window.setInterval(tick, 1000);
}

/* ---------- Boot ---------- */

function boot(scope) {
  initProducts(scope);
  initBeforeAfter(scope);
  initPromoCountdown();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot(document));
} else {
  boot(document);
}

// Theme editor: re-init when a section is re-rendered.
document.addEventListener('shopify:section:load', (event) => boot(/** @type {HTMLElement} */ (event.target)));
